import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as api from '../api.ts';
import { useMapRegion, useMapSubstrate, type MapContent } from '../map-layer.tsx';
import type { MapRegion } from '../map-style.ts';
import {
  layoutWarnings,
  warningPoints,
  WARNING_CLUSTER_PREFIX,
  WARNING_PIN_PREFIX,
} from '../lib/warning-map.ts';
import { Button, Empty, GlassPanel, Status } from '../ui.tsx';
import { WarningKindIcon } from '../warning-icons.tsx';
import {
  isMuted,
  kindOf,
  useWarningFilter,
  WarningsFilterControl,
  type WarningFilter,
} from '../warning-filter.tsx';
import type { Screen } from '../shell.tsx';

/**
 * Home: the operational surface floating over the platform's one persistent map.
 *
 * The map is no longer Home's — it belongs to the shell (`map-layer.tsx`) and is
 * live under every screen. Home is what floats over it *here*: it makes the pins
 * interactive (tap one for the building card, click empty map to place an
 * unplaced building), and it stacks the live picture in a macOS-notification
 * strip at the right — running events, unacknowledged escalations, and the one
 * readiness nudge. There is no global building/estate switcher any more (owner
 * decision 2026-09-08): filtering is per-screen (People/Teams/Activity carry
 * their own Site filter), the map picks a sensible default and is *focused* by
 * tapping a pin here, and — because that focus must never be a one-way door —
 * a multi-site org gets an "All buildings" control to return to the estate
 * roll-up. Every building's pin (red when it is alerting) stays on the map
 * regardless of focus, so an incident anywhere is always visible.
 *
 * The right strip is **active events and things-needing-attention only**. The
 * closed-drill history that used to sit in its lower half is gone — history is
 * the Activity screen's job, not the live strip's. Raising an alert is a control
 * in the chrome (the rail), so it is deliberately not repeated here.
 */
export function Home({
  orgId,
  onOpen,
  onNavigate,
  canSetUp,
  workMobileAsk,
}: {
  orgId: string;
  onOpen: (eventId: string) => void;
  /**
   * Send the operator to the screen that fixes a to-do row, with the building
   * it belongs to selected first.
   */
  onNavigate: (screen: Screen, siteId: string) => void;
  /**
   * Whether the signed-in member can actually set this organisation up — the
   * same admin-role check the first-run gate in `main.tsx` uses.
   */
  canSetUp: boolean;
  /**
   * The person still owes a work mobile (0138). On Home the shell's banner
   * would sit under the map's fixed controls with its clicks passing through
   * to the map, so the ask is one more card in the strip, opening Settings →
   * Your work mobile.
   */
  workMobileAsk?: { onOpen: () => void };
}) {
  // The one persistent map's shared state: every building, which are running
  // something, the current estate/building focus, and the hook to make the map
  // interactive from here.
  const { sites, view, estateCapable, alerting, setView, setInteraction, setContent, project } =
    useMapSubstrate();

  // A warning the operator clicked in the strip: the map frames its area until
  // they pick a building or return to the estate, which hand the camera back.
  const [warningFocus, setWarningFocus] = useState<WarningFocus | null>(null);
  // The person's own filter over both warning surfaces (migration 0136):
  // kinds hidden from the map, and kinds muted from the strip.
  const warningFilter = useWarningFilter();

  const events = useQuery({
    queryKey: ['events', orgId],
    queryFn: () => api.listEvents(orgId),
    // The always-on map status has to stay live: without polling, a just-raised
    // incident's red pin and its "happening" card can go stale while the tab is
    // focused. 30s aligns with the escalations query in NotificationsStrip.
    refetchInterval: 30_000,
  });

  /**
   * Active incidents: an alert has actually gone out and people are being
   * accounted for. `pending_second_confirm` is deliberately NOT here — nothing
   * has been sent for a pending event, so counting it as an active incident
   * asserts a state that is not true (the recurring "screen says something
   * launched when it did not" bug). Pending is its own category below.
   */
  const running = useMemo(
    () => (events.data?.events ?? []).filter((e) => e.state === 'active'),
    [events.data],
  );

  /**
   * Awaiting authorisation: raised, unsent, waiting on a second admin (R1).
   * It is still the most time-critical thing the dashboard can show — the
   * authorisation lapses on its own — so it sits at the top of the strip, but
   * as its own category rather than blended into the active incidents.
   */
  const awaiting = useMemo(
    () => (events.data?.events ?? []).filter((e) => e.state === 'pending_second_confirm'),
    [events.data],
  );

  // The place-a-building flow, and the currently-open pin card.
  const { placingId, setPlacingId, onMapClick, placeError } = usePlacing(orgId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Register this screen's interaction with the shared map: a pin tap opens the
  // building card, and — only while a placement is in progress — a map click
  // writes the coordinate. The handlers read the latest state through refs, so
  // the registration itself is stable and never remounts or reframes the map.
  const mapClickRef = useRef(onMapClick);
  mapClickRef.current = onMapClick;
  useEffect(() => {
    setInteraction({
      onSelect: (id) => {
        setWarningFocus(null);
        setSelectedId(id);
      },
      onMapClick: (latitude, longitude) => mapClickRef.current?.(latitude, longitude),
    });
    // Leaving Home hands the map back to its ambient state on every other screen.
    return () => setInteraction({});
  }, [setInteraction]);

  const unplaced = useMemo(
    () => sites.filter((s) => s.latitude == null || s.longitude == null),
    [sites],
  );

  // The selected building. Every building is on the map regardless of focus, so a
  // pin card can open for any of them; a building that is deleted out from under
  // an open card drops the selection and closes it rather than stranding it.
  const selectedSite = selectedId ? (sites.find((s) => s.id === selectedId) ?? null) : null;

  // An org with no buildings: the map (the shell's) is still a base canvas, with
  // the honest next step floating over it in glass.
  if (sites.length === 0) {
    return (
      // `pointer-events-auto`: Home renders inside `<main pointer-events-none>`
      // (the bleed pass-through that lets map gestures reach the substrate), so
      // without opting back in this whole panel — the primary "Set up this
      // organisation" button included — is dead to clicks (owner fix
      // 2026-09-07).
      <div className="pointer-events-auto relative z-10 px-6 pt-6 pb-16">
        <GlassPanel className="w-full max-w-[680px]">
          <div className="px-6 py-6">
            <Empty
              action={
                canSetUp ? (
                  <Button variant="primary" onClick={() => onNavigate('setup', '')}>
                    Set up this organisation
                  </Button>
                ) : undefined
              }
            >
              {canSetUp
                ? 'No buildings yet. Set up your organisation to add the first one and start running drills against it.'
                : 'No buildings yet. An administrator needs to set this organisation up before there is anything to show here.'}
            </Empty>
          </div>
        </GlassPanel>
      </div>
    );
  }

  return (
    <>
      {/* The way back out of a single-building focus. Focusing centres the map on
          one building (tapping its pin), and without this the persisted view
          could strand a multi-site operator on one building with no control to
          return to the estate roll-up — a one-way door. It survives the persisted
          view: whatever building focus was restored, this is always reachable. */}
      {estateCapable && view !== 'estate' ? (
        <EstateReturn
          onReturn={() => {
            setWarningFocus(null);
            setView('estate');
          }}
        />
      ) : null}

      <NotificationsStrip orgId={orgId} running={running} awaiting={awaiting} onOpen={onOpen}>
        {/* The readiness nudge moved off the bottom-left of the map and into the
            strip, as the owner asked — one more macOS-style card among what
            needs attention, not a pill covering the map. */}
        {workMobileAsk ? (
          <Notice
            tone="attention"
            title="Add your work mobile"
            detail="So your organisation can reach you during an alert"
            onClick={workMobileAsk.onOpen}
            actionLabel="Add your work mobile in Settings"
          />
        ) : null}
        <SetupNudge key={orgId} orgId={orgId} sites={sites} onNavigate={onNavigate} />
        {/* An agency's warning covering one of this org's buildings — the
            Weather-pattern sentence, one more card among what needs attention.
            Attributed to the agency (W1), and it opens nothing that raises:
            a warning never enters the activation path (W3). */}
        <WarningsCards
          key={`warnings-${orgId}`}
          orgId={orgId}
          focusedId={warningFocus?.id ?? null}
          onFocus={setWarningFocus}
          filter={warningFilter}
        />
      </NotificationsStrip>

      {/* Browse-everything: the agencies' in-force warnings drawn on the map
          around the estate, pull not push (ratified 2026-09-26). */}
      <WarningsLayer
        sites={sites}
        setContent={setContent}
        project={project}
        focus={warningFocus}
        onFocus={setWarningFocus}
        onHide={() => setWarningFocus(null)}
        filter={warningFilter}
        buildingOpen={selectedSite !== null}
        onOpenWarning={() => setSelectedId(null)}
      />

      {selectedSite ? (
        <PinCard
          orgId={orgId}
          site={selectedSite}
          alerting={alerting.has(selectedSite.name)}
          project={project}
          onOpen={() => {
            setView(selectedSite.id);
            setSelectedId(null);
          }}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      <PlaceOverlay
        unplaced={unplaced}
        placingId={placingId}
        placeError={placeError}
        onStart={setPlacingId}
        onCancel={() => setPlacingId(null)}
        // The bottom-centre pin card and this bottom-right affordance cannot
        // both fit below ~1032px, so while a pin card is open (and a placement
        // is not mid-flight) this yields on narrow screens and returns on lg.
        pinCardOpen={selectedSite !== null}
      />
    </>
  );
}

/* ------------------------------------------------------ estate-return control */

/**
 * "All buildings" — the return control out of a single-building focus, top-left
 * over the map.
 *
 * The rail's estate/building switcher was removed (owner decision 2026-09-08),
 * so focusing the map on one building (by tapping its pin) needs an explicit way
 * back to the estate roll-up. Without it a persisted single-building view is a
 * one-way door: the operator can drill into a building and never get back to the
 * whole-estate view. It renders only for a multi-site org (`estateCapable`) that
 * is currently focused on a building, and returns to `'estate'`, which the
 * substrate persists.
 *
 * A worded control, never an icon-only one (`design-dashboard.md` §7.3), and a
 * pointer-events island because Home's `<main>` is `pointer-events-none` so map
 * gestures fall through to the substrate. It sits top-left, clear of the
 * notifications strip (top-right) and, on desktop, of the fixed 240px rail.
 */
function EstateReturn({ onReturn }: { onReturn: () => void }) {
  return (
    <div className="pointer-events-auto fixed top-16 left-4 z-20 lg:top-14 lg:left-[256px]">
      <GlassPanel elevation="medium" className="notif-in">
        <button
          type="button"
          onClick={onReturn}
          aria-label="Show all buildings — return to the estate overview"
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius-1)] px-3 text-[14px] font-medium text-[var(--ink)] hover:bg-[var(--paper-3)]"
        >
          <span aria-hidden>&lsaquo;</span>
          All buildings
        </button>
      </GlassPanel>
    </div>
  );
}

/* --------------------------------------------------------------- the pin card */

/** Pixels from the pin's coordinate to the callout's pointer tip — the 28px
 *  landmark avatar's radius plus a little air, so the tip sits on the pin's
 *  edge and never covers it (`map-style.ts` `markerAnchor`). */
const CALLOUT_GAP = 20;
/** Keep the callout this far from every viewport edge while it follows a pin. */
const CALLOUT_MARGIN = 12;
/** Room the callout leaves at the top for the chrome before it flips below. */
const CALLOUT_TOP_CLEARANCE = 64;

/**
 * The building callout. A tap on a pin opens this ON the pin — the Apple Maps
 * shape: a card with a pointer whose tip sits on the building, following it as
 * the map pans and zooms, flipping below the pin when there is no room above,
 * and sliding sideways (the pointer staying on the pin) near a screen edge.
 * It carries what the old estate table did: name, status, how many follow it,
 * and any readiness gaps, with a way straight into that building.
 *
 * It follows the pin by asking the map where the coordinate is on screen every
 * frame (`project`, installed by whichever engine is mounted) and writing the
 * position straight to the element — no React render per frame. When the pin
 * pans off screen the callout goes with it, as Apple's does; tapping the pin
 * again brings it back. Where no projection exists (the map-unavailable panel)
 * it docks at the foot of the screen as it used to, so it can always be read
 * and closed.
 *
 * The on-site line is the hard rule (G20): it appears **only** when the
 * building actually measures presence — a `presence` basis, meaning the number
 * came from people signed in as present. A roster fallback is not who is here,
 * so the line is omitted entirely rather than showing a count that reads as
 * on-site and is not. Most sites have no presence; for them there is simply no
 * line.
 */
function PinCard({
  orgId,
  site,
  alerting,
  project,
  onOpen,
  onClose,
}: {
  orgId: string;
  site: api.Site;
  alerting: boolean;
  project: (latitude: number, longitude: number) => { x: number; y: number } | null;
  onOpen: () => void;
  onClose: () => void;
}) {
  const readiness = useQuery({
    queryKey: ['readiness', orgId, site.id],
    queryFn: () => api.getReadiness(orgId, site.id),
  });
  const presence = useQuery({
    queryKey: ['presence', orgId, site.id],
    queryFn: () => api.getPresence(orgId, site.id),
  });

  const r = readiness.data;
  const p = presence.data;
  // Non-null ONLY when the count is real presence (check-ins), never the roster
  // fallback. `null` → no line at all (G20).
  const onSite = p && p.basis === 'presence' ? p.counts.total : null;

  return (
    <MapCallout project={project} latitude={site.latitude} longitude={site.longitude}>
      <GlassPanel
        elevation="high"
        role="dialog"
        aria-label={`${site.name} details`}
        className="notif-in px-4 py-3.5"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[16px] leading-[20px] font-[650] text-[var(--ink)]">{site.name}</h2>
          <button
            type="button"
            aria-label={`Close ${site.name} details`}
            onClick={onClose}
            className="-mt-1 -mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[16px] text-[var(--ink-2)] hover:bg-[var(--paper-3)] hover:text-[var(--ink)]"
          >
            <span aria-hidden>&times;</span>
          </button>
        </div>

        <div className="mt-1.5">
          {alerting ? (
            <Status tone="alert">Something running</Status>
          ) : !r ? (
            <span className="text-[13px] text-[var(--ink-2)]">Checking readiness…</span>
          ) : r.ready ? (
            <Status tone="safe">Ready</Status>
          ) : (
            <Status tone="attention">
              {r.gaps.length} {r.gaps.length === 1 ? 'gap' : 'gaps'}
            </Status>
          )}
        </div>

        <dl className="mt-2.5 space-y-1 text-[13px]">
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--ink-2)]">Following</dt>
            <dd className="tabular text-[var(--ink)]">{site.followers}</dd>
          </div>
          {/* On site only when it is measured — never a guessed or roster number. */}
          {onSite !== null ? (
            <div className="flex justify-between gap-4">
              <dt className="text-[var(--ink-2)]">On site</dt>
              <dd className="tabular text-[var(--ink)]">{onSite}</dd>
            </div>
          ) : null}
        </dl>

        {/* Readiness gaps, spelled out — a count says there is a problem, the
            list says what it is. Only when there is something outstanding. */}
        {r && !r.ready && r.gaps.length > 0 ? (
          <ul className="mt-2.5 list-none space-y-1 p-0 text-[13px] text-[var(--ink-2)]">
            {r.gaps.slice(0, 3).map((g) => (
              <li key={g} className="flex gap-2">
                <span aria-hidden className="text-[var(--attention)]">
                  •
                </span>
                <span>{g}</span>
              </li>
            ))}
            {r.gaps.length > 3 ? (
              <li className="text-[var(--ink-2)]">and {r.gaps.length - 3} more, on the building</li>
            ) : null}
          </ul>
        ) : null}

        <div className="mt-3">
          <Button variant="primary" full label={`Open ${site.name}`} onClick={onOpen}>
            Open
          </Button>
        </div>
      </GlassPanel>
    </MapCallout>
  );
}

/**
 * A card ON a map point — the Apple Maps shape: a pointer whose tip sits on the
 * point, following it as the map pans and zooms, flipping below the point when
 * there is no room above, and sliding sideways (the pointer staying on the
 * point) near a screen edge. Where no projection exists (the map-unavailable
 * panel) it docks at the foot of the screen, so it can always be read and
 * closed. The building card and the warning card both ride it.
 *
 * It asks the map where the coordinate is every frame (`project`) and writes
 * the position straight to the element — no React render per frame.
 */
function MapCallout({
  project,
  latitude,
  longitude,
  children,
}: {
  project: (latitude: number, longitude: number) => { x: number; y: number } | null;
  latitude: number | null;
  longitude: number | null;
  children: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  // Docked = no projection to anchor to; the card sits bottom-centre instead.
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    const lat = latitude;
    const lon = longitude;
    let frame = 0;
    let wasDocked: boolean | null = null;
    const tick = () => {
      const wrap = wrapRef.current;
      const arrow = arrowRef.current;
      const at = lat != null && lon != null ? project(lat, lon) : null;
      const dock = at === null;
      if (dock !== wasDocked) {
        wasDocked = dock;
        setDocked(dock);
      }
      if (wrap && arrow && at) {
        const w = wrap.offsetWidth;
        const h = wrap.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const onScreen = at.x >= 0 && at.x <= vw && at.y >= 0 && at.y <= vh;
        // Above the point by default; below it when the chrome would clip the top.
        const below = at.y - CALLOUT_GAP - h < CALLOUT_TOP_CLEARANCE;
        const left = Math.min(Math.max(at.x - w / 2, CALLOUT_MARGIN), vw - w - CALLOUT_MARGIN);
        const top = below ? at.y + CALLOUT_GAP : at.y - CALLOUT_GAP - h;
        wrap.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
        wrap.style.visibility = onScreen ? 'visible' : 'hidden';
        wrap.dataset.side = below ? 'below' : 'above';
        // The pointer stays over the point even when the card slides to fit.
        arrow.style.left = `${Math.min(Math.max(at.x - left, 22), w - 22)}px`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [project, latitude, longitude]);

  return (
    <div
      ref={wrapRef}
      // Anchored: fixed at the viewport origin and moved by transform each frame;
      // hidden until the first frame has placed it, so it never flashes at 0,0.
      // Docked: the old bottom-centre card.
      className={
        docked
          ? 'pointer-events-auto fixed bottom-6 left-1/2 z-30 w-[min(320px,calc(100vw-2rem))] -translate-x-1/2'
          : 'group pointer-events-auto fixed top-0 left-0 z-30 w-[min(300px,calc(100vw-2rem))]'
      }
      style={docked ? undefined : { visibility: 'hidden' }}
    >
      {/* The pointer: a rotated square of the same glass inside a clip box on
          whichever edge faces the point, so only the half outside the card
          shows (a whole square would lay a second pane of glass over the card). */}
      <div
        ref={arrowRef}
        aria-hidden
        className={
          docked
            ? 'hidden'
            : 'absolute top-full h-2.5 w-6 -translate-x-1/2 overflow-hidden group-data-[side=below]:top-auto group-data-[side=below]:bottom-full'
        }
      >
        <div className="sheet absolute top-[-8px] left-1/2 h-3.5 w-3.5 -translate-x-1/2 rotate-45 border border-[var(--glass-edge)] group-data-[side=below]:top-[4px]" />
      </div>
      {children}
    </div>
  );
}

/* ------------------------------------------------------- notifications strip */

/**
 * The floating notifications strip, top-right of the map: a light, translucent,
 * bounded column of stacked glass cards — never a full-height rail, never
 * covering the map. macOS-notification style: each card is click-through to the
 * place it is about.
 *
 * It carries **what is happening now and what needs attention, and nothing
 * else**: running events and unsent authorisations, then escalations a human at
 * the centre has not yet acknowledged, then whatever needs-attention cards are
 * passed in (the readiness nudge). The old "Recently" record of closed drills is
 * gone — history is the Activity screen, not the live strip. When nothing is
 * live it stays a single calm line and invents nothing.
 */
function NotificationsStrip({
  orgId,
  running,
  awaiting,
  onOpen,
  children,
}: {
  orgId: string;
  /** Active incidents — an alert has gone out and people are being counted. */
  running: api.EventListItem[];
  /** Raised but unsent, waiting on a second admin (R1). A distinct category. */
  awaiting: api.EventListItem[];
  onOpen: (eventId: string) => void;
  /** Needs-attention cards that are not live events — the readiness nudge. */
  children?: React.ReactNode;
}) {
  const escalations = useQuery({
    queryKey: ['escalations', orgId],
    queryFn: () => api.getEscalations(orgId),
    refetchInterval: 30_000,
  });
  const openEscalations = (escalations.data?.escalations ?? []).filter(
    (e) => e.direction === 'inbound' && !e.acknowledged_at,
  );

  const quiet = running.length === 0 && awaiting.length === 0 && openEscalations.length === 0;

  return (
    <div
      role="log"
      aria-label="What is happening"
      // pointer-events-auto: Home's <main> is pointer-events-none so map gestures
      // reach the substrate, so this island must opt back in to stay clickable.
      // lg:top-14 (not top-4) leaves the top-right corner for the appearance
      // toggle in the chrome — the strip sits below it, never under it.
      className="pointer-events-auto fixed top-16 right-4 z-20 flex max-h-[calc(100vh-6rem)] w-[min(300px,calc(100vw-2rem))] flex-col gap-2 overflow-y-auto lg:top-14"
    >
      {/* Awaiting authorisation — above active incidents, because the
          authorisation lapses and nothing has been sent yet (R1). It must never
          read as an alert that went out. One notification per event. */}
      {awaiting.map((e) => (
        <Notice
          key={e.id}
          tone="attention"
          title={`Awaiting authorisation — ${e.site_name}`}
          detail="Raised — nothing has been sent until a second admin authorises it"
          onClick={() => onOpen(e.id)}
          actionLabel={`Open ${e.site_name}`}
        />
      ))}

      {running.map((e, i) => (
        <Notice
          key={e.id}
          tone="alert"
          delayMs={i * 90}
          title={`${e.site_name} — ${e.mode === 'drill' ? 'a drill is running' : 'an emergency is running'}`}
          detail={`${e.safe} of ${e.notified} answered`}
          onClick={() => onOpen(e.id)}
          actionLabel={`Open ${e.site_name}`}
        />
      ))}

      {openEscalations.map((e) => (
        <Notice
          key={e.id}
          tone="alert"
          title={`Waiting to be acknowledged — ${e.occupier_name ?? 'A tenant'}${e.site_name ? ` (${e.site_name})` : ''}`}
          detail={`${e.body} Paging repeats until the centre acknowledges it.`}
        />
      ))}

      {/* Needs-attention notifications that are not live events (the readiness
          nudge, the warnings covering this org's buildings). */}
      {children}

      {quiet ? (
        <GlassPanel elevation="medium" role="status" className="px-3.5 py-2.5">
          {/* "No active incidents", not "Nothing is happening right now" — the
              latter contradicts a readiness nudge (an attention card) sitting
              directly above it, which speaks to things that DO need attention.
              This line is only ever about live incidents. */}
          <p className="text-[13px] text-[var(--ink-2)]">No active incidents.</p>
        </GlassPanel>
      ) : null}
    </div>
  );
}

/**
 * One notification in the strip — the macOS shape: a compact glass card, a
 * tone dot, a one-line title and a one-line detail, the whole card the target
 * when it goes somewhere. Each thing that needs attention is its own card;
 * nothing is grouped under a header into one tall block.
 *
 * Both lines truncate to one line each so every card is the same calm height;
 * the full text stays in the accessible name and the hover title, so nothing
 * is lost by the ellipsis.
 */
function Notice({
  tone,
  icon,
  title,
  detail,
  onClick,
  actionLabel,
  selected = false,
  delayMs = 0,
}: {
  tone: 'alert' | 'attention';
  /** A glyph in place of the tone dot (the warning kind). */
  icon?: React.ReactNode;
  title: string;
  detail?: string;
  onClick?: () => void;
  /** The accessible name when the card is a button; defaults to the text. */
  actionLabel?: string;
  /** Pressed state — the warning whose area the map is framing. */
  selected?: boolean;
  delayMs?: number;
}) {
  const full = detail ? `${title}. ${detail}` : title;
  const body = (
    <span className="flex min-w-0 items-start gap-2.5">
      <span
        aria-hidden
        className={`mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center ${
          tone === 'alert' ? 'text-[var(--alert)]' : 'text-[var(--attention)]'
        }`}
      >
        {icon ?? <span className="h-2 w-2 rounded-full bg-current" />}
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-[13px] leading-[18px] font-[600] text-[var(--ink)]">
          {title}
        </span>
        {detail ? (
          <span className="block truncate text-[12px] leading-[16px] text-[var(--ink-2)]">
            {detail}
          </span>
        ) : null}
      </span>
      {onClick ? (
        <span aria-hidden className="mt-[1px] shrink-0 text-[15px] text-[var(--ink-2)]">
          &rsaquo;
        </span>
      ) : null}
    </span>
  );
  return (
    <GlassPanel
      elevation="medium"
      style={delayMs ? { animationDelay: `${delayMs}ms` } : undefined}
      className={`notif-in shrink-0 ${selected ? 'outline outline-2 outline-[var(--attention)]' : ''}`}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          title={full}
          aria-label={actionLabel ? `${actionLabel}: ${full}` : full}
          aria-pressed={selected || undefined}
          className="block w-full rounded-[var(--radius-3)] px-3.5 py-2.5 hover:bg-[var(--paper-3)]"
        >
          {body}
        </button>
      ) : (
        <div title={full} aria-label={full} className="px-3.5 py-2.5">
          {body}
        </div>
      )}
    </GlassPanel>
  );
}

/* ------------------------------------------------------------------- place */

/**
 * The "place it on the map" affordance, ON the map. A building with no
 * coordinates is on the roster and not on the canvas, which the map cannot say
 * for itself. This floats over the map: pick an unplaced building, then click
 * where it is, and the coordinates are written straight back through
 * `updateSite`.
 *
 * `placingId` is lifted so the same click handler Home registers on the shared
 * map can route a tap to this mutation. Nothing is invented: the pin lands
 * exactly where the admin clicked (G20).
 */
function PlaceOverlay({
  unplaced,
  placingId,
  placeError = null,
  onStart,
  onCancel,
  pinCardOpen = false,
}: {
  unplaced: api.Site[];
  placingId: string | null;
  /** A refused placement in the server's words (e.g. a pin on another building). */
  placeError?: string | null;
  onStart: (siteId: string) => void;
  onCancel: () => void;
  /** True while a pin card is open — collides with this below lg (see Home). */
  pinCardOpen?: boolean;
}) {
  // Declared before the early return so the hook count is stable whether or not
  // there is anything to place.
  const [query, setQuery] = useState('');

  if (unplaced.length === 0) return null;
  const placing = unplaced.find((s) => s.id === placingId);
  // Yield to the pin card on narrow screens, but never while a placement is
  // mid-flight — hiding the Cancel button would strand the operator.
  const yieldBelowLg = pinCardOpen && !placing;

  const q = query.trim().toLowerCase();
  const matches = q ? unplaced.filter((s) => s.name.toLowerCase().includes(q)) : unplaced;

  return (
    // While a placement is in progress the panel collapses to a single line and
    // a Cancel — so a corner strip stays out of the way and the rest of the map
    // is a live, clickable target. The picker is capped in height and scrolls,
    // so a long estate can never grow it into a wall over the canvas.
    <div
      className={`pointer-events-auto fixed right-4 bottom-4 z-20 w-[min(320px,calc(100vw-2rem))] ${
        yieldBelowLg ? 'hidden lg:block' : ''
      }`}
    >
      <GlassPanel elevation="medium" role="status" aria-label="Buildings not on the map">
        <div className="px-3.5 py-3">
          {placing ? (
            <>
              <p className="mb-2 text-[14px] text-[var(--ink)]">
                Click where <strong>{placing.name}</strong> is on the map.
              </p>
              {placeError ? (
                <p className="mb-2 text-[14px] text-[var(--attention)]">{placeError}</p>
              ) : null}
              <Button variant="quiet" label={`Cancel placing ${placing.name}`} onClick={onCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <p className="mb-2 text-[13px] text-[var(--ink-2)]">
                {unplaced.length === 1
                  ? '1 building is not on the map yet.'
                  : `${unplaced.length} buildings are not on the map yet.`}{' '}
                Pick one, then click where it is.
              </p>
              {/* A search box appears once the list outgrows a glance — below
                  that a filter is more friction than the four buttons it hides. */}
              {unplaced.length > 4 ? (
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a building"
                  aria-label="Find a building to place on the map"
                  className="mb-2 h-8 w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-2.5 text-[13px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
                />
              ) : null}
              <div className="flex max-h-[210px] flex-col gap-1.5 overflow-y-auto">
                {matches.map((s) => (
                  <Button
                    key={s.id}
                    variant="secondary"
                    full
                    label={`Place ${s.name} on the map`}
                    onClick={() => onStart(s.id)}
                  >
                    Place {s.name}
                  </Button>
                ))}
                {matches.length === 0 ? (
                  <p className="py-1 text-[13px] text-[var(--ink-2)]">
                    No building matches “{query.trim()}”.
                  </p>
                ) : null}
              </div>
            </>
          )}
        </div>
      </GlassPanel>
    </div>
  );
}

/** Shared click-to-place state + mutation. */
function usePlacing(orgId: string) {
  const qc = useQueryClient();
  const [placingId, setPlacingId] = useState<string | null>(null);
  const place = useMutation({
    mutationFn: (p: { siteId: string; latitude: number; longitude: number }) =>
      api.updateSite(orgId, p.siteId, { latitude: p.latitude, longitude: p.longitude }),
    onSuccess: () => {
      setPlacingId(null);
      void qc.invalidateQueries({ queryKey: ['sites', orgId] });
    },
  });
  const onMapClick = placingId
    ? (latitude: number, longitude: number) =>
        place.mutate({ siteId: placingId, latitude, longitude })
    : undefined;
  // A pin dropped onto another of this org's buildings is refused (409
  // site_duplicate) — said in the server's words, never a silent no-op.
  const placeError = placingId && place.error ? place.error.message : null;
  return { placingId, setPlacingId, onMapClick, placeError };
}

/* ------------------------------------------------------------- setup nudge */

/**
 * The one readiness nudge Home carries: a compact, dismissible card that points
 * an organisation with outstanding readiness gaps at the Readiness screen. It
 * snoozes against the gap count so a one-time dismissal can never bury a future
 * gap, and it routes to Readiness (the one screen that lists and fixes every
 * kind of gap) rather than the wizard.
 *
 * It renders as a card *in the notifications strip* now, not as a bottom-left
 * pill over the map (the owner's call: what needs attention belongs in the
 * top-right strip). The card shape and its role="status" are unchanged, so it
 * still reads to a screen reader as a status region.
 */
const NUDGE_SNOOZE_PREFIX = 'evac.home-setup-nudge:';

function readNudgeSnooze(orgId: string): number | null {
  try {
    const raw = localStorage.getItem(`${NUDGE_SNOOZE_PREFIX}${orgId}`);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeNudgeSnooze(orgId: string, count: number): void {
  try {
    localStorage.setItem(`${NUDGE_SNOOZE_PREFIX}${orgId}`, String(count));
  } catch {
    // The redirect re-fires next load — a mild annoyance, not a trap.
  }
}

export function SetupNudge({
  orgId,
  sites,
  onNavigate,
}: {
  orgId: string;
  sites: api.Site[];
  onNavigate: (screen: Screen, siteId: string) => void;
}) {
  const results = useQueries({
    queries: sites.map((site) => ({
      queryKey: ['readiness', orgId, site.id],
      queryFn: () => api.getReadiness(orgId, site.id),
    })),
  });
  const [snoozedAt, setSnoozedAt] = useState<number | null>(() => readNudgeSnooze(orgId));

  const loaded = results.map((r) => r.data).filter((d): d is api.Readiness => Boolean(d));
  const count = loaded.reduce((n, d) => n + d.gaps.length, 0);

  const snoozed = snoozedAt !== null && count <= snoozedAt;
  if (snoozed || loaded.length === 0 || count === 0) return null;

  const dismiss = () => {
    writeNudgeSnooze(orgId, count);
    setSnoozedAt(count);
  };

  return (
    <GlassPanel
      elevation="medium"
      role="status"
      aria-label="Readiness reminder"
      className="notif-in flex items-center gap-1 py-1.5 pr-1.5 pl-3.5"
    >
      <button
        type="button"
        onClick={() => onNavigate('readiness', '')}
        className="inline-flex min-h-[32px] items-center gap-1 text-[14px] font-medium text-[var(--accent)] underline-offset-2 hover:underline"
      >
        {count === 1 ? '1 thing needs attention' : `${count} things need attention`}
        <span aria-hidden>&rsaquo;</span>
      </button>
      <button
        type="button"
        aria-label="Dismiss the readiness reminder"
        onClick={dismiss}
        className="ml-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[16px] text-[var(--ink-2)] hover:bg-[var(--paper-3)] hover:text-[var(--ink)]"
      >
        <span aria-hidden>&times;</span>
      </button>
    </GlassPanel>
  );
}

/* ------------------------------------------------------------ public warnings */

/** A warning the operator asked the map to show: its id and the box to frame. */
interface WarningFocus {
  id: string;
  latitude: number;
  longitude: number;
  span: number;
}

/**
 * The agency's name as it reads in a one-line card. "Australian Government
 * Bureau of Meteorology" is correct and forty characters; the attribution (W1)
 * survives the trim because the agency is still named — only the
 * jurisdiction's preamble goes. Anything unrecognised is shown whole.
 */
function shortAgency(source: string): string {
  return source.replace(/^Australian Government\s+/i, '').replace(/^the\s+/i, '');
}

/**
 * The first line of a warning card: the agency's own headline, cut at the
 * point where it starts naming places — " — " on the road feeds ("CRASH 2
 * cars — Sydney Harbour Tunnel"), the first " for " on the Bureau's ("Strong
 * Wind Warning for Port Phillip…"). The words are the agency's, only
 * shortened; the place goes on the second line from the agency's own area
 * text. `cap_event` alone ("Wind", "CRASH") is too terse to read as a warning.
 */
function warningTitle(w: api.PublicWarning): string {
  const h = w.headline.trim();
  const dash = h.indexOf(' — ');
  if (dash > 0) return h.slice(0, dash);
  const forAt = h.search(/\sfor\s/i);
  if (forAt > 0) return h.slice(0, forAt);
  return h || w.cap_event || 'Warning';
}

/**
 * The agencies' warnings that cover this org's own buildings — the sentence
 * `docs/multi-agency-warnings.md` names as the operator's actual question:
 * not "what is the weather", but "does any warning cover MY buildings".
 *
 * One notification per warning, two lines each: what it is (the agency's own
 * event name), then where (the agency's own area words) and who said it. The
 * buildings it covers are in the accessible name and hover text; the card's
 * click frames the warning's area on the map, where those buildings' pins sit
 * inside the amber. Attribution stays on every card (W1), the tone is the
 * attention amber and never red (red is this org's own live emergency), and
 * nothing here raises anything — a warning never enters, prefills or gates the
 * activation path (W3).
 *
 * The same warning re-issued by the agency arrives as a new document with the
 * same words; those collapse to one card (the latest to expire), because four
 * identical "Strong Wind Warning for Port Phillip" cards say one thing four
 * times.
 */
function WarningsCards({
  orgId,
  focusedId,
  onFocus,
  filter,
}: {
  orgId: string;
  focusedId: string | null;
  onFocus: (f: WarningFocus | null) => void;
  /** The person's filter: a muted kind raises no card here. */
  filter: WarningFilter;
}) {
  const matchesQuery = useQuery({
    queryKey: ['warning-matches', orgId, filter.showMinor],
    queryFn: () => api.getWarningMatches(orgId, filter.showMinor),
    // A warning expires on the issuer's clock; a covered building must not
    // keep wearing it after the agency stood it down.
    refetchInterval: 60_000,
  });
  const all = matchesQuery.data?.matches ?? [];
  // Held back until the preference is read, so a muted card never flashes in
  // and out. A failed read settles too — everything shows, never nothing.
  if (all.length === 0 || !filter.settled) return null;
  // One key for "the same warning", so the card count and the muted count can
  // never disagree about how many warnings there are.
  const warningKey = (m: api.WarningMatch) =>
    `${m.source}|${warningTitle(m)}|${m.area_description ?? m.headline}`;
  // An Emergency Warning or Watch and Act is never muted (isMuted's floor).
  const matches = all.filter((m) => !isMuted(m, filter.muted));
  // Muting quiets the card; it never pretends the warning is not there.
  const mutedCount = new Set(all.filter((m) => isMuted(m, filter.muted)).map(warningKey)).size;

  const byWarning = new Map<string, { warning: api.WarningMatch; siteNames: string[] }>();
  for (const m of matches) {
    const key = warningKey(m);
    const entry = byWarning.get(key) ?? { warning: m, siteNames: [] };
    if (m.expires_at > entry.warning.expires_at) entry.warning = m;
    if (!entry.siteNames.includes(m.site_name)) entry.siteNames.push(m.site_name);
    byWarning.set(key, entry);
  }
  const grouped = [...byWarning.values()];
  const LIMIT = 4;

  return (
    <>
      {grouped.slice(0, LIMIT).map(({ warning, siteNames }) => {
        const what = warningTitle(warning);
        const where = warning.area_description ?? siteNames.join(', ');
        const focused = focusedId === warning.id;
        return (
          <Notice
            key={warning.id}
            tone="attention"
            icon={<WarningKindIcon kind={warning.event_kind} />}
            title={what}
            detail={`${where} · ${shortAgency(warning.source)}`}
            selected={focused}
            actionLabel={`Show on the map — ${warning.source}: ${warning.headline}. Covers ${siteNames.join(', ')}`}
            onClick={() => {
              if (focused) {
                onFocus(null);
                return;
              }
              const latSpan = warning.max_latitude - warning.min_latitude;
              const lonSpan = warning.max_longitude - warning.min_longitude;
              onFocus({
                id: warning.id,
                latitude: (warning.min_latitude + warning.max_latitude) / 2,
                longitude: (warning.min_longitude + warning.max_longitude) / 2,
                // A little air around the area, and never a street-level zoom
                // for a warning that names a whole district.
                span: Math.max(latSpan, lonSpan, 0.05) * 1.3,
              });
            }}
          />
        );
      })}
      {grouped.length > LIMIT ? (
        <GlassPanel elevation="medium" className="notif-in shrink-0 px-3.5 py-2">
          <p className="text-[12px] text-[var(--ink-2)]">
            {grouped.length - LIMIT} more {grouped.length - LIMIT === 1 ? 'warning' : 'warnings'} —
            each affected building's page carries its own.
          </p>
        </GlassPanel>
      ) : null}
      {mutedCount > 0 ? (
        <GlassPanel elevation="medium" className="notif-in shrink-0 px-3.5 py-2">
          <p className="text-[12px] text-[var(--ink-2)]">
            {mutedCount} muted {mutedCount === 1 ? 'warning covers' : 'warnings cover'} your
            buildings — each building's page still shows it.
          </p>
        </GlassPanel>
      ) : null}
    </>
  );
}

/**
 * The in-force warnings drawn on the persistent map — the browse-everything
 * layer (ratified 2026-09-26): pull, never push. Fatigue attaches to
 * interruptions, so looking at the weather is something the operator does, not
 * something done to them. Browsing adds no push; the one warning push (W2 as
 * amended 2026-09-29) tells dashboard users about their own buildings, and is
 * the worker's, not this map's.
 *
 * Drawn by the PHONE's rules (`lib/warning-map.ts`), so the two maps agree:
 * each warning's own shapes (its real polygons first, else the districts it
 * names, never the union box), a road closure as a pin with no area and only
 * at suburb zoom, one icon pin per warning in its tier's colour on its largest
 * shape, overlapping pins collapsed into a counted badge, and a tap that opens
 * the agency's own words. Merged OVER the estate pins and never part of the
 * camera's framing, so an incident anywhere still turns its building red under
 * this layer and a storm never drags the camera off the estate.
 *
 * Fetched for what the map is showing once the camera settles (the phone's
 * browse rule); before the map has said where it is, for the estate's
 * surroundings.
 */
function WarningsLayer({
  sites,
  setContent,
  project,
  focus,
  onFocus,
  onHide,
  filter,
  buildingOpen,
  onOpenWarning,
}: {
  sites: api.Site[];
  setContent: (c: MapContent | null) => void;
  project: (latitude: number, longitude: number) => { x: number; y: number } | null;
  /** A warning picked in the strip — the camera frames it while set. */
  focus: WarningFocus | null;
  /** Frame something (a cluster being opened). */
  onFocus: (f: WarningFocus | null) => void;
  /** Every kind was hidden; a focus on a warning nobody can see is dropped. */
  onHide: () => void;
  /** The person's filter: a hidden kind is not drawn. */
  filter: WarningFilter;
  /** A building's card is open — one card at a time, so the warning's closes. */
  buildingOpen: boolean;
  /** A warning's card opened — the building's closes. */
  onOpenWarning: () => void;
}) {
  const region = useMapRegion();
  const estateBox = useMemo(() => {
    const placed = sites.filter((s) => s.latitude != null && s.longitude != null);
    if (placed.length === 0) return null;
    const lats = placed.map((s) => s.latitude as number);
    const lons = placed.map((s) => s.longitude as number);
    // ±2° (~200km) around the estate, clamped so a coastal estate never
    // sends the API an out-of-range coordinate it would 400.
    return {
      minLatitude: Math.max(Math.min(...lats) - 2, -90),
      maxLatitude: Math.min(Math.max(...lats) + 2, 90),
      minLongitude: Math.max(Math.min(...lons) - 2, -180),
      maxLongitude: Math.min(Math.max(...lons) + 2, 180),
    };
  }, [sites]);
  // What to ask for: the visible map, padded a tenth each way so a small pan
  // is already loaded, rounded so a settle that moved nothing asks nothing new.
  const box = useMemo(() => {
    if (!region) return estateBox;
    const padLat = (region.maxLatitude - region.minLatitude) * 0.1;
    const padLon = (region.maxLongitude - region.minLongitude) * 0.1;
    const r = (v: number) => Math.round(v * 1000) / 1000;
    return {
      minLatitude: r(Math.max(region.minLatitude - padLat, -90)),
      maxLatitude: r(Math.min(region.maxLatitude + padLat, 90)),
      minLongitude: r(Math.max(region.minLongitude - padLon, -180)),
      maxLongitude: r(Math.min(region.maxLongitude + padLon, 180)),
    };
  }, [region, estateBox]);

  const inForce = useQuery({
    queryKey: ['warnings-in', box, filter.showMinor],
    queryFn: () => api.warningsIn(box as NonNullable<typeof box>, filter.showMinor),
    enabled: box !== null,
    refetchInterval: 60_000,
    // A pan keeps the pins it had until the new answer lands — never a blink
    // to an empty sky between two settles.
    placeholderData: keepPreviousData,
  });
  const all = useMemo(() => inForce.data?.warnings ?? [], [inForce.data]);
  // What the map draws: every kind the person has not hidden — and the one
  // warning picked in the strip, whatever its kind, because showing where a
  // warning is means drawing it.
  const focusId = focus?.id ?? null;
  const warnings = useMemo(
    () => all.filter((w) => !filter.hidden.has(kindOf(w)) || w.id === focusId),
    [all, filter.hidden, focusId],
  );
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of all) m.set(kindOf(w), (m.get(kindOf(w)) ?? 0) + 1);
    return m;
  }, [all]);

  // Laid out for the settled viewport. Before the map has reported one, the
  // fetch box at a desktop's size — zoomed out, so no road closures yet.
  const layout = useMemo(() => {
    const at: MapRegion | null = region ?? (box ? { ...box, widthPx: 1280, heightPx: 800 } : null);
    return at ? layoutWarnings(warnings, at, focusId) : null;
  }, [warnings, region, box, focusId]);

  // The open card: one warning, or the list for pins stacked on one spot.
  const [open, setOpen] = useState<{
    ids: string[];
    at: { latitude: number; longitude: number };
  } | null>(null);
  useEffect(() => {
    if (buildingOpen) setOpen(null);
  }, [buildingOpen]);
  const openWarnings = useMemo(
    () => (open ? all.filter((w) => open.ids.includes(w.id)) : []),
    [open, all],
  );

  // Read by the tap handler, which is registered once per layout.
  const onOpenWarningRef = useRef(onOpenWarning);
  onOpenWarningRef.current = onOpenWarning;
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  useEffect(() => {
    const camera = focus
      ? { latitude: focus.latitude, longitude: focus.longitude, span: focus.span }
      : undefined;
    if (!layout || (layout.singles.length === 0 && layout.clusters.length === 0)) {
      setContent(camera ? { focus: camera } : null);
      return;
    }
    const onSelectPoint = (id: string) => {
      if (id.startsWith(WARNING_PIN_PREFIX)) {
        const warningId = id.slice(WARNING_PIN_PREFIX.length);
        const d = layout.singles.find((x) => x.id === warningId);
        if (!d) return;
        setOpen({ ids: [warningId], at: d.at });
        onOpenWarningRef.current();
        return;
      }
      const c = layout.clusters.find((x) => `${WARNING_CLUSTER_PREFIX}${x.id}` === id);
      if (!c) return;
      const latSpread = c.maxLatitude - c.minLatitude;
      const lonSpread = c.maxLongitude - c.minLongitude;
      // Pins stacked on one point (several warnings for the same district)
      // never separate however far in, so those open as a list instead.
      if (latSpread <= 1e-4 && lonSpread <= 1e-4) {
        setOpen({ ids: c.warnings.map((w) => w.id), at: c.at });
        onOpenWarningRef.current();
        return;
      }
      // Zoom until the cluster's pins separate.
      const current = region ? region.maxLatitude - region.minLatitude : 1;
      setOpen(null);
      onFocusRef.current({
        id: `${WARNING_CLUSTER_PREFIX}${c.id}`,
        latitude: (c.minLatitude + c.maxLatitude) / 2,
        longitude: (c.minLongitude + c.maxLongitude) / 2,
        span: Math.min(Math.max(Math.max(latSpread, lonSpread) * 1.8, 0.01), current / 2),
      });
    };
    setContent({
      points: warningPoints(layout),
      polygons: layout.polygons,
      focus: camera,
      onSelectPoint,
    });
    return () => setContent(null);
  }, [layout, setContent, focus, region]);

  if (!box) return null;

  return (
    <>
      <div className="pointer-events-auto fixed bottom-6 left-4 z-20 lg:left-[256px]">
        <WarningsFilterControl
          filter={filter}
          shownCount={warnings.length}
          totalCount={all.length}
          counts={counts}
          onHideAll={onHide}
        />
      </div>
      {open && openWarnings.length > 0 ? (
        <WarningCallout
          warnings={openWarnings}
          at={open.at}
          project={project}
          onPick={(id) => setOpen({ ids: [id], at: open.at })}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}

/**
 * A tapped warning, ON its pin: the agency's own words — the first line of its
 * headline, who said it and until when (W1: the agency first, never us), then
 * its description and instruction as written. Nothing here raises anything (W3).
 * For pins stacked on one spot, the list of them; picking one opens it.
 */
function WarningCallout({
  warnings,
  at,
  project,
  onPick,
  onClose,
}: {
  warnings: api.BrowseWarning[];
  at: { latitude: number; longitude: number };
  project: (latitude: number, longitude: number) => { x: number; y: number } | null;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const one = warnings.length === 1 ? (warnings[0] ?? null) : null;
  const heading = one ? warningTitle(one) : `${warnings.length} warnings here`;
  return (
    <MapCallout project={project} latitude={at.latitude} longitude={at.longitude}>
      <GlassPanel
        elevation="high"
        role="dialog"
        aria-label={one ? `${one.source}: ${one.headline}` : heading}
        className="notif-in px-4 py-3.5"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex items-start gap-2 text-[16px] leading-[20px] font-[650] text-[var(--ink)]">
            {one ? (
              <WarningKindIcon
                kind={one.event_kind}
                className="mt-0.5 shrink-0 text-[var(--attention)]"
              />
            ) : null}
            <span>{heading}</span>
          </h2>
          <button
            type="button"
            aria-label={`Close ${heading}`}
            onClick={onClose}
            className="-mt-1 -mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[16px] text-[var(--ink-2)] hover:bg-[var(--paper-3)] hover:text-[var(--ink)]"
          >
            <span aria-hidden>&times;</span>
          </button>
        </div>
        {one ? (
          <div className="mt-1 max-h-[45vh] overflow-y-auto text-[13px] leading-[18px]">
            <p className="text-[var(--ink-2)]">
              {shortAgency(one.source)} · until {untilText(one.expires_at)}
            </p>
            {one.area_description ? (
              <p className="mt-1.5 text-[var(--ink-2)]">{one.area_description}</p>
            ) : null}
            {one.description ? (
              <p className="mt-2 whitespace-pre-line text-[var(--ink)]">{one.description}</p>
            ) : null}
            {one.instruction ? (
              <p className="mt-2 whitespace-pre-line text-[var(--ink)]">{one.instruction}</p>
            ) : null}
          </div>
        ) : (
          <ul className="mt-2 max-h-[45vh] list-none space-y-1 overflow-y-auto p-0">
            {warnings.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  onClick={() => onPick(w.id)}
                  className="flex w-full items-start gap-2 rounded-[var(--radius-1)] px-1.5 py-1.5 text-left hover:bg-[var(--paper-3)]"
                >
                  <WarningKindIcon
                    kind={w.event_kind}
                    className="mt-0.5 shrink-0 text-[var(--attention)]"
                  />
                  <span className="text-[13px] leading-[18px]">
                    <span className="block font-medium text-[var(--ink)]">{warningTitle(w)}</span>
                    <span className="block text-[var(--ink-2)]">{shortAgency(w.source)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </GlassPanel>
    </MapCallout>
  );
}

/** "Fri 4:30 pm" — when the agency says the warning stops being in force. */
function untilText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}
